import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Injectable, Signal, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Deltakerstatus, GruppeId, GruppeMedlemListeModell, HtmlTekst, MedlemId, MøtedeltakerModell, MøteId, SendInnkallingSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {tomGruppeModell, tomMøteModell} from "felles-ng2/modell/tommeModeller";
import {combineLatestWith, map, Observable} from "rxjs";
import {MøteDatatjeneste, MøteModellDLM} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatToolbar} from "@angular/material/toolbar";
import {MeldingSendtIkonKomponent} from "web-ng2/delt/komponenter/melding-sendt-ikon/melding-sendt-ikon";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {TranslateModule} from "@ngx-translate/core";
import {VelgMedlemmerKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer/velg-medlemmer";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {GruppeDatatjeneste, GruppeModellDML} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatTooltip} from "@angular/material/tooltip";
import {MedlemListevisningKompaktKomponent} from "../../../medlem/komponenter/medlem-listevisning-kompakt/medlem-listevisning-kompakt";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {medlemmerSomDeltarPåMøte} from "felles-ng2/modell/møtedeltakerModellHjelpere";
import {PotensieltPromise, somPromise} from "felles-ng2/støttefunksjoner/promiser";

const debug = initDebugger('SendMøteinnkallingDialogKomponent');

/**
 * Dialog for å sende møteinnkalling
 */
@Component({
    selector: 'send-moeteinnkalling-dialog',
    templateUrl: 'send-møteinnkalling-dialog.html',
    styleUrl: 'send-møteinnkalling-dialog.scss',
    host: {'data-testid': 'send-møteinnkalling-dialog'},
    imports: [
        FormsModule,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatPrefix,
        MatProgressBar,
        MatSlideToggle,
        MatToolbar,
        MeldingSendtIkonKomponent,
        TipsKomponent,
        TranslateModule,
        VelgMedlemmerKomponent,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        MatCheckbox,
        MatTooltip,
        MedlemListevisningKompaktKomponent,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendMøteinnkallingDialogKomponent {

    static vis(dialog: MatDialog, møte: PotensieltPromise<MøteModellDLM>, møtedeltakere: PotensieltPromise<MøtedeltakerModell[]>, forhåndsvalgteMottakere: MedlemId[] | null = null): MatDialogRef<SendMøteinnkallingDialogKomponent, SendMøteinnkallingDialogRespons> {
        return dialog.open(SendMøteinnkallingDialogKomponent, {data: {møte: somPromise(møte), møtedeltakere: somPromise(møtedeltakere), forhåndsvalgteMottakere} as DialogParametre, disableClose: true, ariaLabelledBy: 'send-møteinnkalling-dialog-tittel', minHeight: 500, maxWidth: 700});
    }

    moete = signal<MøteModellDLM>(tomMøteModell());
    moetedeltakere = signal<MøtedeltakerModell[]>([]);
    gruppe = signal<GruppeModellDML>(tomGruppeModell());
    inkluderAgendaEndret: Signal<boolean>;
    modus = signal<'skjema' | 'kvittering'>('skjema');
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    skjema = new FormGroup({
        beskjed: new FormControl<HtmlTekst>('', {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
        valgteMottakere: new FormControl<GruppeMedlemListeModell[]>([], {nonNullable: true}),
        inkluderAgenda: new FormControl(false, {nonNullable: true}),
        huskInkluderAgendaValg: new FormControl(false, {nonNullable: true}),
        publiserMoete: new FormControl(false, {nonNullable: true})
    });
    tilgjengeligeMottakere: GruppeMedlemListeModell[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<SendMøteinnkallingDialogKomponent, SendMøteinnkallingDialogRespons>,
                private avhengigheter: SendMøteinnkallingDialogKomponentAvhengigheter,
                private enumOversettelser: EnumOversettelser,
                private oversettelser: Oversettelser,
                private navigasjon: Navigasjon,
                private destroyRef: DestroyRef) {
        this.initMøte(parametre.møte, parametre.møtedeltakere, parametre.forhåndsvalgteMottakere);

        this.inkluderAgendaEndret = toSignal(this.skjema.controls.inkluderAgenda.valueChanges
          .pipe(
            combineLatestWith(toObservable(this.gruppe)),
            map(([inkluderAgenda, gruppe]) => inkluderAgenda != gruppe.oppsett.inkluderAgendaIInnkalling)), {initialValue: false});
    }

    invitasjonsStatus(medlem: GruppeMedlemListeModell): string {
        const deltaker = this.finnDeltaker(medlem);
        return erIkkeNil(deltaker) ?
          this.oversettelser.oversett(i18n('Innkalling {status}'), {status: this.enumOversettelser.oversettDeltakerstatus(deltaker.status)}) :
          this.oversettelser.oversett(i18n('Ikke innkalt'));
    }

    erInvitasjonIkkeSendt(medlem: GruppeMedlemListeModell): boolean {
        const deltaker = this.finnDeltaker(medlem);
        return erIkkeNil(deltaker) ? deltaker.status == Deltakerstatus.innkalling_ikke_sendt : false;
    }

    async publiser() {
        await this.lagrer.utfør(async () => {
            await this.sendInnkalling();
            this.modus.set('kvittering');
        });
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    gaaTilHendelseslogg() {
        this.ferdig();
        this.navigasjon.gåTilMøteHendelseslogg(this.moete().id);
    }

    //private hjelpemetoder

    private async initMøte(møtepromise: Promise<MøteModellDLM>, møtedeltakere: Promise<MøtedeltakerModell[]>, forhåndsvalgteMottakere: MedlemId[] | null) {
        await this.laster.utfør(async () => {
            this.moete.set(await møtepromise);
            this.moetedeltakere.set(await møtedeltakere);
        });
        //trenger ikke å vente på gruppelasting...
        this.avhengigheter.gruppe(this.moete().eiendeGruppeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(gruppe => this.initGruppe(gruppe));
        const laster = this.laster.start();
        this.avhengigheter.medlemmer(this.moete().eiendeGruppeId)
          .pipe(
            laster.avsluttVedLeveranse(),
            takeUntilDestroyed(this.destroyRef))
          .subscribe(medlemmer => this.initTilgjengeligeMottakere(medlemmer, forhåndsvalgteMottakere));
    }

    private async initGruppe(gruppe: GruppeModellDML) {
        this.gruppe.set(gruppe);
        this.skjema.controls.inkluderAgenda.setValue(gruppe.oppsett.inkluderAgendaIInnkalling);
    }

    private initTilgjengeligeMottakere(medlemmer: GruppeMedlemListeModell[], forhåndsvalgteMottakere: MedlemId[] | null) {
        this.tilgjengeligeMottakere = medlemmer;

        const valgte = erIkkeNil(forhåndsvalgteMottakere) ?
          medlemmer.filter(medlem => forhåndsvalgteMottakere.includes(medlem.id)) :
          medlemmerSomDeltarPåMøte(this.moetedeltakere(), medlemmer);
        this.skjema.controls.valgteMottakere.setValue(valgte);
    }

    private finnDeltaker(medlem: GruppeMedlemListeModell): MøtedeltakerModell | null {
        return this.moetedeltakere().find(d => d.medlem.id === medlem.id) ?? null;
    }

    private async sendInnkalling() {
        await this.lagrer.utfør(() => this.avhengigheter.sendInnkalling(this.moete().id, this.somSpesifikasjon()));
    }

    private somSpesifikasjon(): SendInnkallingSpesifikasjon {
        const verdier = this.skjema.value;
        return {
            beskjed: verdier.beskjed!,
            mottakere: verdier.valgteMottakere!.map(medlem => medlem.id),
            inkluderAgenda: verdier.inkluderAgenda!,
            huskInkluderAgendaValg: verdier.huskInkluderAgendaValg!,
            publiserMoete: verdier.publiserMoete!
        };
    }
}

export type SendMøteinnkallingDialogRespons = boolean;

export type DialogParametre = {
    møte: Promise<MøteModellDLM>;
    møtedeltakere: Promise<MøtedeltakerModell[]>;
    /** Dersom ikke-null vil angitte medlemmer være forhåndsvalgt, dersom null vil deltakere på møte bli satt som forhåndsvalgte */
    forhåndsvalgteMottakere: MedlemId[] | null;
}

@Injectable()
export class SendMøteinnkallingDialogKomponentAvhengigheter {

    constructor(private møteDatatjeneste: MøteDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste, private gruppeDatatjeneste: GruppeDatatjeneste) {
    }

    medlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilgjengelige(gruppeId);
    }

    gruppe(gruppeId: GruppeId): Observable<GruppeModellDML> {
        return this.gruppeDatatjeneste.hent(gruppeId);
    }

    async sendInnkalling(id: MøteId, spesifikasjon: SendInnkallingSpesifikasjon): Promise<void> {
        await this.møteDatatjeneste.sendInnkalling(id, spesifikasjon);
    }
}
